import { graphql } from '@apollo/client/react/hoc/graphql';
import { ApolloQueryResult } from '@apollo/client';
import {
  LanguageSelector,
  hideIntercomLauncher,
  showIntercomLauncher,
  theme,
  updateIntercomUser,
  waitFor,
} from '@arnold/common';
import styled from '@emotion/styled/macro';
import compose from 'lodash.flowright';
import React, { ComponentType, ReactElement, useEffect, useState } from 'react';
import Div100vh from 'react-div-100vh';
import { RouteComponentProps, withRouter } from 'react-router';
import { Modals } from '../chat/ChatWindow';
import { Header } from '../components';
import ChangeLanguage from '../components/modals/ChangeLanguage';
import {
  AccessTokenStatus,
  ContactType,
  Exact,
  IsAccessTokenValidQuery,
  IsAccessTokenValidQueryResult,
  IsAccessTokenValidQueryVariables,
  ReportSubscriptionSubscription,
  UserOrgRole,
} from '../generated/hooks';
import { isAccessTokenValidQuery } from '../graphql/queries';
import auth from '../lib/auth';
import { parseUrlParams } from '../lib/common';
import { useIsMobileView } from '../reports/components/ResponsiveSwitcher';
import { IChannel } from '../types';
import UserMenu from './UserMenu';
import { UserProvider } from './providers/UserProvider';

interface IProps extends RouteComponentProps<any> {
  user?: IsAccessTokenValidQuery['isAccessTokenValid']['user'];
  children: ReactElement;
  setLocalization: (lang: string) => any;
  history: any;
  location: any;
  defaultModal?: Modals;
  allowedLanguages?: string[];
  initLanguage?: string | null;
  navbarContent?: React.ReactNode;
  someReport?: boolean;
  hideIntercomLauncher?: boolean;
  data: {
    isAccessTokenValid: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']>;
    refetch: (
      variables?: Partial<Exact<IsAccessTokenValidQueryVariables>> | undefined,
    ) => Promise<ApolloQueryResult<IsAccessTokenValidQuery>>;
  };
}

const ControlsContainer = styled.div`
  display: flex;
  gap: 0.7rem;
`;

const PageLayout = (props: IProps) => {
  const isMobile = useIsMobileView();
  const [modal, setModal] = useState<Modals | undefined>(props.defaultModal);
  const [hasAnswered, toggleLanguageSelector] = useState<boolean>(false);
  const [selectedLanguage, handleChangeLanguage] = useState<string>(props.initLanguage!);
  const [channel, setChannel] = useState<IChannel | null>(null);
  const [reportRespondent, setReportRespondent] =
    useState<NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent']>(null);
  const [redirect, setRedirect] = useState<string | null>(null);

  const params = parseUrlParams(props.location.search);
  useEffect(() => {
    const userEmail = params.getParam('userEmail');
    if (userEmail) {
      auth.redirectToLogin(userEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportRespondent?.user?.username) {
      auth.setUserEmail(reportRespondent.user.username);
    }
  }, [reportRespondent?.user?.username]);

  useEffect(() => {
    const updateIntercomLauncher = async () => {
      await waitFor(() => typeof window.Intercom === 'function', undefined, undefined, undefined, true);
      if (typeof window.Intercom !== 'function') {
        return;
      }

      try {
        const intercomStatus = window.Intercom;
        if (!intercomStatus.booted) {
          await waitFor(() => intercomStatus.booted);
        }
        if (props.hideIntercomLauncher) {
          hideIntercomLauncher();
        } else {
          showIntercomLauncher();
        }
      } catch (error) {
        console.error('Error checking Intercom status:', error);
      }
    };

    updateIntercomLauncher();
  }, [props.hideIntercomLauncher]);

  useEffect(() => {
    const { user } = props;
    if (user && user.respondents) {
      const respondentLanguage = user.respondents.find((r) => r.language?.code)?.language?.code;
      const normalized = respondentLanguage?.split('-')[0]?.toLowerCase();
      const allowed = props.allowedLanguages && normalized ? props.allowedLanguages.includes(normalized) : true;
      if (normalized && allowed) {
        changeLanguage(normalized);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showModal = (modalName: Modals, show: boolean, redirectTo?: string) => () => {
    setModal(show ? modalName : undefined);
    if (!show && redirect) {
      props.history.push(redirect);
    }
    if (show && redirectTo) {
      setRedirect(redirectTo);
    } else {
      setRedirect(null);
    }
  };

  const changeLanguage = (languageCode: string) => {
    handleChangeLanguage(languageCode);
    if (languageCode) {
      props.setLocalization(languageCode);

      if (props.user?.respondents?.find((r) => r.organizationRole === UserOrgRole.OrgAdmin)) {
        updateIntercomUser({
          language_override: languageCode,
          user_hash: auth.getIntercomHash(),
        });
      }
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLElement> | null) => {
    if (auth.isLoggedIn()) {
      props.history.push('/overview');
    } else if (channel && channel.respondent) {
      if (!channel.respondent.user) {
        auth.redirectToLogin(channel.respondent.contacts?.find((c) => c.type === ContactType.Email)?.value);
      } else {
        props.history.push(`/chat-overview/${channel.id}`);
      }
    } else {
      props.history.push('/overview');
      auth.redirectToLogin();
    }
  };

  const hideDropdown = params.getParam('hideDropdown') === 'true';
  return (
    <UserProvider user={props.data.isAccessTokenValid} refetch={props.data.refetch}>
      <Div100vh
        style={{
          height: '100rvh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f9f9f9',
        }}
      >
        <ChatHeader>
          {channel !== null ? (
            <Header
              channel={channel}
              disabled={hasAnswered}
              isPublic={channel.public}
              showModal={showModal}
              onLogoClick={handleLogoClick}
              hideDropdown={hideDropdown}
              someReport={props.someReport}
            />
          ) : (
            <Row>
              <Brand>
                {isMobile ? (
                  <img src="/arnold-icon.svg" width={42} height={42} alt="Arnold" onClick={handleLogoClick} />
                ) : (
                  <img src="/arnold-logo.svg" width={192} height={48} alt="Arnold" onClick={handleLogoClick} />
                )}
              </Brand>
              {props.navbarContent}
              <ControlsContainer>
                {selectedLanguage && (
                  <LanguageSelector
                    lang={selectedLanguage}
                    allowedLanguages={Object.freeze(props.allowedLanguages)}
                    onChange={changeLanguage}
                  />
                )}

                {!hideDropdown && (
                  <UserMenu
                    reportRespondent={reportRespondent}
                    channel={channel}
                    showModal={showModal}
                    user={props.user || props.data.isAccessTokenValid?.user}
                    someReport={props.someReport || false}
                  />
                )}
              </ControlsContainer>
            </Row>
          )}
        </ChatHeader>
        {React.cloneElement(props.children, {
          showModal,
          modal,
          hasAnswered,
          toggleLanguageSelector,
          changeLanguage,
          setChannel,
          setReportRespondent,
          selectedLanguage,
        })}
        <ChangeLanguage
          show={!!channel && modal === Modals.CHANGE_LANGUAGE}
          showModal={showModal}
          changeLanguage={changeLanguage}
          selectedLanguage={selectedLanguage}
          disabled={hasAnswered}
          id={channel?.id}
          finished={!!channel?.isFinished}
          allowedLanguages={channel?.allowedLanguages || []}
        />
      </Div100vh>
    </UserProvider>
  );
};

const Brand = styled.div`
  cursor: pointer;

  @media (min-width: ${theme.breakpoint.small}) {
    width: 126px;
  }

  @media (min-width: 992px) {
    width: 192px;
  }

  img {
    height: auto;
    display: block;

    @media (min-width: ${theme.breakpoint.small}) {
      width: 100%;
    }

    @media (min-width: 992px) {
      margin-top: -6px;
    }
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

export const HEADER_HEIGHT_MOBILE = '56px';
export const HEADER_HEIGHT = '64px';

const ChatHeader = styled.div`
  height: ${HEADER_HEIGHT_MOBILE};
  background-color: #fff;
  z-index: 214748369;
  justify-content: space-between;
  align-items: center;
  display: flex;
  padding-left: 20px;
  flex-shrink: 0;

  @media (min-width: 576px) {
    height: ${HEADER_HEIGHT};
    padding-left: 40px;
    padding-right: 20px;
  }
`;

export default compose(
  withRouter,
  graphql<
    { user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']>['user'] },
    IsAccessTokenValidQueryResult,
    IsAccessTokenValidQueryVariables
  >(isAccessTokenValidQuery, {
    options: () => ({
      notifyOnNetworkStatusChange: true,
      skip: !auth.isLoggedIn(),
      onError: () => {
        auth.removeUserCookies();
      },
      onCompleted: (data) => {
        // @ts-ignore
        // Consent is now implied at login; restore consent check when removing this workaround.
        if (data?.isAccessTokenValid.status !== AccessTokenStatus.Valid) {
          auth.removeUserCookies();
        }
      },
    }),
  }),
)(PageLayout) as PageLayoutWithoutRouterAndGqlProps;

type PageLayoutWithoutRouterAndGqlProps = ComponentType<Omit<IProps, 'history' | 'location' | 'match' | 'data'>>;
